import {createDivIn, createHeaderIn, createImgIn, createParagraphIn, setElementOnClick} from '../ui_utils/DomUtils.ts'
import {LobbyImage, ColonistMembershipType} from '@colonist/types'
import {type ColonistMembershipPerk, ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct.ts'
import {UIPopupController} from '../ui_popup/UIPopupController.ts'
import {type TranslationObject} from '@colonist/i18n'
import {ColonistMembershipPerkType} from '@colonist/lobby-types'

export class UIMembershipProductView {
    readonly membershipProduct: ColonistMembershipProduct
    readonly productDiv: HTMLDivElement
    readonly bodyDiv: HTMLDivElement
    readonly headerDiv: HTMLDivElement

    constructor(container: HTMLDivElement, membershipProduct: ColonistMembershipProduct) {
        this.membershipProduct = membershipProduct
        this.productDiv = createDivIn(container, 'store_page_membership', '')

        const headerDivClass = 'store_page_membership_header ' + this.membershipProduct.membershipColor
        this.headerDiv = createDivIn(this.productDiv, headerDivClass, '')
        createImgIn(this.headerDiv, this.membershipProduct.lobbyImage, 'header_background_image')
        createImgIn(this.headerDiv, this.membershipProduct.lobbyImage)
        createHeaderIn(this.headerDiv, this.membershipProduct.membershipTitle, '', '', 'H2')

        this.bodyDiv = createDivIn(this.productDiv, 'store_page_membership_body', '')
        this.createPerks()
        this.addBestValueHint()
    }

    private createPerks() {
        const bodyGridDiv = createDivIn(this.bodyDiv, 'store_page_membership_grid', '')
        const topMembership = ColonistMembershipProduct.getMembershipProduct(ColonistMembershipType.EliteMonthly)

        const allPerks = this.getValidMembershipPerks(topMembership)
        for(const perk of allPerks) this.createPerkIn(bodyGridDiv, perk)
    }

    private createPerkIn(container: HTMLDivElement, perk: ColonistMembershipPerk) {
        const perkCheckDiv = createDivIn(container, '', '')
        const perkCheckIcon = createImgIn(perkCheckDiv, perk.imageUrl)
        const perkTextDiv = createDivIn(container, '', '')
        const perkText = createParagraphIn(perkTextDiv, perk.description, '', '')
        const infoIcon = UIMembershipProductView.createInfoIcon(container, perk)
        if(!this.isPerkActive(perk)) {
            perkCheckIcon.style.display = 'none'
            perkText.style.opacity = '50%'
            infoIcon.style.opacity = '50%'
        }
    }

    protected addBestValueHint() {
        if(!this.membershipProduct.bestValue) return

        this.productDiv.classList.add('store_page_membership_best_value_border')
        const badge = createDivIn(this.headerDiv, 'badge-white badge-lg')
        createParagraphIn(badge, {key: 'strings:storePage.bestValue'}, '')
    }

    // eslint-disable-next-line class-methods-use-this
    protected getValidMembershipPerks(membership: ColonistMembershipProduct): ColonistMembershipPerk[] {
        const perks = structuredClone(membership.perks)

        for(const inactivePerks of membership.perks) {
            if(perks.some((perk) => perk.type == inactivePerks.type)) continue
            if(inactivePerks.type == ColonistMembershipPerkType.FullReplay) continue

            perks.push(inactivePerks)
        }

        UIMembershipProductView.mergeMapsAndExpansionDescriptions(perks)
        return perks
    }

    protected static mergeMapsAndExpansionDescriptions(perks: ColonistMembershipPerk[]): ColonistMembershipPerk[] {
        const mapPerk = perks.find((perk) => perk.type == ColonistMembershipPerkType.AllMaps)
        const expansionPerk = perks.find((perk) => perk.type == ColonistMembershipPerkType.AllExpansions)
        if(!mapPerk || !expansionPerk) return perks

        const mapPerkIndex = perks.indexOf(mapPerk)
        perks.splice(mapPerkIndex, 1)
        expansionPerk.description = {key: 'strings:storePage.sections.membership.perks.allMapsAndExpansions'}
        return perks
    }

    private isPerkActive(perk: ColonistMembershipPerk): boolean {
        return this.membershipProduct.perks.some((activePerk: ColonistMembershipPerk) => activePerk.type == perk.type)
    }

    private static createInfoIcon(parentDiv: HTMLDivElement, perk: ColonistMembershipPerk): HTMLDivElement {
        const perkInfoDiv = createDivIn(parentDiv, '', '')

        const perksWithInfoIcon = [
            ColonistMembershipPerkType.AllExpansions,
            ColonistMembershipPerkType.AllMaps,
            ColonistMembershipPerkType.BasicReplay,
            ColonistMembershipPerkType.FullReplay,
            ColonistMembershipPerkType.PremiumMatchmaking,
        ]
        const shouldShowIcon = perksWithInfoIcon.includes(perk.type)
        if(!shouldShowIcon) return perkInfoDiv

        const perkInfoIcon = createImgIn(perkInfoDiv, LobbyImage.InfoIcon)
        UIMembershipProductView.createClickEventForInfoIcon(perkInfoIcon, perk.type)
        return perkInfoDiv
    }

    private static createClickEventForInfoIcon(infoIcon: HTMLImageElement, perkType: ColonistMembershipPerkType) {
        if(perkType == ColonistMembershipPerkType.PremiumMatchmaking) {
            setElementOnClick(infoIcon, () => UIMembershipProductView.createInfoPopupForPremiumMatchmaking(), false, true)
            return
        }
        setElementOnClick(infoIcon, () => UIMembershipProductView.createInfoPopupForMapsAndExpansions(), false, true)
    }

    private static createInfoPopupForPremiumMatchmaking() {
        const popupTitle: TranslationObject = {key: 'strings:storePage.sections.membership.perks.premiumMatchmaking.title'}
        const popupBody: TranslationObject = {key: 'strings:storePage.sections.membership.perks.premiumMatchmaking.description'}
        UIPopupController.create(popupTitle,
            popupBody,
            () => undefined,
            () => undefined,
            true,
            {showXonHeader: true, showXButton: false, showCheckButton: false})
    }

    private static createInfoPopupForMapsAndExpansions() {
        const popupTitle: TranslationObject = {key: 'strings:storePage.FAQ.title'}
        const popupBody: TranslationObject = {key: 'strings:storePage.FAQ.body'}
        UIPopupController.create(popupTitle,
            popupBody,
            () => undefined,
            () => undefined,
            true,
            {showXonHeader: true, showXButton: false, showCheckButton: false})
    }
}
